import { useEffect, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { ArrowLeft, ArrowRight, Loader2, Siren } from "lucide-react";
import { api, errorText } from "../lib/api";
import { useLang } from "../i18n/LangContext";
import { useSettings } from "../context/SettingsContext";
import { useSeo } from "../components/site/Seo";
import { useWizardDraft, validateStep, buildPayload } from "../components/wizard/useWizard";
import { Progress } from "../components/wizard/Progress";
import { StepService } from "../components/wizard/StepService";
import { StepLocation } from "../components/wizard/StepLocation";
import { StepVehicle } from "../components/wizard/StepVehicle";
import { StepContact } from "../components/wizard/StepContact";
import { Summary } from "../components/wizard/Summary";
import { Confirmation } from "../components/wizard/Confirmation";

const DONE_KEY = "tow-last-confirmation";

export default function RequestPage() {
  const { t, lang } = useLang();
  const { settings, loaded } = useSettings();
  const [params, setParams] = useSearchParams();
  const { draft, setDraft, update, reset } = useWizardDraft();
  const [errors, setErrors] = useState({});
  const [submitError, setSubmitError] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [result, setResult] = useState(() => JSON.parse(sessionStorage.getItem(DONE_KEY) || "null"));
  const [maxStep, setMaxStep] = useState(draft.step);
  const topRef = useRef(null);
  const submitLock = useRef(false);

  useSeo(`${t("wizard.title")} | ${settings.business_name}`, t("hero.note"));

  useEffect(() => {
    const svc = params.get("service");
    if (svc && loaded && settings.enabled_services.includes(svc)) {
      sessionStorage.removeItem(DONE_KEY);
      setResult(null);
      setDraft((d) => ({ ...d, service_id: svc, step: Math.max(d.step, 1) }));
      setMaxStep((m) => Math.max(m, 1));
      setParams({}, { replace: true });
    }
  }, [params, loaded, settings.enabled_services, setDraft, setParams]);

  const go = (step) => {
    setErrors({});
    setSubmitError("");
    setDraft((d) => ({ ...d, step }));
    setMaxStep((m) => Math.max(m, step));
    topRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  const next = () => {
    const e = validateStep(draft.step, draft, settings);
    setErrors(e);
    if (Object.keys(e).length) {
      setTimeout(() => document.querySelector('[aria-invalid="true"], [role="alert"]')?.scrollIntoView({ block: "center" }), 50);
      return;
    }
    go(draft.step + 1);
  };

  const jump = (step) => {
    for (let s = 0; s < step; s += 1) {
      if (Object.keys(validateStep(s, draft, settings)).length) return go(s);
    }
    return go(step);
  };

  const submit = async () => {
    if (submitLock.current) return;
    for (let s = 0; s < 3; s += 1) {
      const e = validateStep(s, draft, settings);
      if (Object.keys(e).length) {
        go(s);
        setErrors(e);
        return;
      }
    }
    submitLock.current = true;
    setSubmitting(true);
    setSubmitError("");
    const payload = { ...buildPayload(draft, settings, lang), website: document.querySelector('input[name="website"]')?.value || "" };
    try {
      const { data } = await api.post("/public/requests", payload);
      const done = {
        ...data,
        pickup: payload.pickup,
        destination: payload.destination,
        needs_destination_help: payload.needs_destination_help,
        customer: payload.customer,
        vehicle: payload.vehicle,
        contact_method: payload.customer.contact_method,
      };
      sessionStorage.setItem(DONE_KEY, JSON.stringify(done));
      setResult(done);
      reset();
      setMaxStep(0);
      window.scrollTo({ top: 0 });
    } catch (err) {
      setSubmitError(errorText(err, t("common.error")));
    } finally {
      submitLock.current = false;
      setSubmitting(false);
    }
  };

  const startNew = () => {
    sessionStorage.removeItem(DONE_KEY);
    setResult(null);
    reset();
  };

  const stepProps = { draft, update, setDraft, errors };
  const hasErrors = Object.keys(errors).length > 0;
  const reviewData = buildPayload(draft, settings, lang);

  return (
    <section className="container-x max-w-3xl py-10 sm:py-16" ref={topRef} data-testid="service-wizard-container">
      {result ? (
        <Confirmation result={result} onNew={startNew} />
      ) : (
        <>
          <h1 className="sr-only">{t("wizard.title")}</h1>
          <Progress step={draft.step} maxStep={maxStep} onJump={jump} />
          <form onSubmit={(e) => { e.preventDefault(); if (draft.step === 2) submit(); else next(); }} noValidate>
            <input type="text" name="website" tabIndex={-1} autoComplete="off" className="hidden" aria-hidden="true" />
            <div key={draft.step} className="rise">
              {draft.step === 0 && <StepService {...stepProps} />}
              {draft.step === 1 && <StepLocation {...stepProps} />}
              {draft.step === 2 && (
                <div className="space-y-10">
                  <h2 className="sr-only">{t("wizard.finalTitle")}</h2>
                  <StepVehicle {...stepProps} />
                  <StepContact {...stepProps} />
                  <div>
                    <h3 className="mb-3 text-sm font-bold uppercase tracking-[0.15em] text-gray-400">{t("wizard.quickReview")}</h3>
                    <Summary data={reviewData} onEdit={go} compact />
                  </div>
                </div>
              )}
            </div>
            {hasErrors && <p role="alert" className="mt-6 text-sm font-semibold text-red-400" data-testid="wizard-error-summary">{t("wizard.fixErrors")}</p>}
            {submitError && <p role="alert" className="mt-6 rounded-lg border border-red-500/50 bg-red-500/10 p-4 text-sm text-red-300" data-testid="wizard-submit-error">{submitError}</p>}
            <div className="mt-10 flex items-center justify-between gap-3">
              {draft.step > 0 ? (
                <button type="button" onClick={() => go(draft.step - 1)} className="btn-ghost px-5 py-3.5" data-testid="wizard-prev-btn">
                  <ArrowLeft className="h-5 w-5" /> {t("wizard.back")}
                </button>
              ) : <span />}
              {draft.step < 2 ? (
                <button type="submit" className="btn-yellow px-7 py-3.5" data-testid="wizard-next-btn">
                  {t("wizard.next")} <ArrowRight className="h-5 w-5" />
                </button>
              ) : (
                <button type="submit" disabled={submitting} className="btn-red flex-1 sm:flex-none whitespace-normal text-center leading-tight px-5 sm:px-8 py-4 text-sm sm:text-base disabled:opacity-60" data-testid="wizard-submit-btn">
                  {submitting ? <Loader2 className="h-5 w-5 animate-spin" /> : <Siren className="h-5 w-5" />}
                  {submitting ? t("wizard.submitting") : t("wizard.submit")}
                </button>
              )}
            </div>
            <p className="mt-6 text-xs text-gray-500">{t("wizard.savedDraft")}</p>
          </form>
        </>
      )}
    </section>
  );
}
